// Layout principal com header, conteúdo e footer
// Alterna entre tela de transferência e lista de carteiras na área principal

import React, { useState, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { WalletConnectionProvider } from '@/contexts/WalletConnectionContext';
import { IncomingTransferProvider } from '@/contexts/IncomingTransferContext';
import { PendingFeeProvider } from '@/contexts/PendingFeeContext';
import { SettingsProvider } from '@/contexts/SettingsContext';
import { useIncomingTransferNotification } from '@/hooks/useIncomingTransferNotification';
import { TransferButtonLogProvider } from '@/contexts/TransferButtonLogContext';
import { NotificationsProvider } from '@/contexts/NotificationsContext';
import TransferNotificationListener from '../notifications/TransferNotificationListener.jsx';
import IncomingTransferReceivedModal from '../modals/IncomingTransferReceivedModal';
import NotificationFloatingButton from '../notifications/NotificationFloatingButton';
import { OpenWalletListProvider } from '@/contexts/OpenWalletListContext';
import PendingFeeModal from '../modals/PendingFeeModal';
import ReconcilePendingTx from '../transfer/ReconcilePendingTx';
import Header from './Header';
import Footer from './Footer';
import LoginScreen from '../auth/LoginScreen';
import TransferForm from '../transfer/TransferForm';
import WalletList from '../wallets/WalletList';
import SettingsScreen from '../settings/SettingsScreen';
import AdminManagement from '../admin/AdminManagement';
import AppOverviewScreen from '../overview/AppOverviewScreen';
import Loader from '../ui/Loader';
import AboutPage from '../about/AboutPage';

/** Tela de estatísticas do projeto — carregada só quando acessada (apenas ambiente local). */
const StatsScreen = lazy(() => import('../stats/StatsScreen'));

const isStatsAvailable = () => import.meta.env.DEV || typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname === 'pcpcryptotransfer.test');

/** Chama o hook de notificação de recebimento (deve estar dentro dos providers). */
function IncomingTransferNotifier() {
  useIncomingTransferNotification();
  return null;
}

export default function MainLayout() {
  const { t } = useTranslation();
  const { isAuthenticated, loading } = useAuth();
  const [showCarteiras, setShowCarteiras] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showAdminManagement, setShowAdminManagement] = useState(false);
  const [showOverview, setShowOverview] = useState(false);
  const [addressToAdd, setAddressToAdd] = useState(null);
  const [walletDataToAdd, setWalletDataToAdd] = useState(null);
  const [showPendingFeeModal, setShowPendingFeeModal] = useState(false);
  const [pendingFeeVariant, setPendingFeeVariant] = useState('blocking');
  const [showAbout, setShowAbout] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <Loader text={t('layout.checkingAuth')} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  return (
    <WalletConnectionProvider>
      <IncomingTransferProvider>
        <PendingFeeProvider>
        <SettingsProvider>
        <TransferButtonLogProvider>
          <NotificationsProvider>
          <OpenWalletListProvider
            openWalletListWithAddress={(address, walletData) => {
              setAddressToAdd(address ?? walletData?.address ?? null);
              setWalletDataToAdd(walletData ?? null);
              setShowCarteiras(true);
            }}
          >
          <IncomingTransferNotifier />
          <TransferNotificationListener />
          <ReconcilePendingTx />
          <IncomingTransferReceivedModal />
          <PendingFeeModal
            isOpen={showPendingFeeModal}
            onClose={() => setShowPendingFeeModal(false)}
            onPaid={() => setShowPendingFeeModal(false)}
            variant={pendingFeeVariant}
          />
          <NotificationFloatingButton />
          <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex flex-col">
            <Header
              onBackToTransfer={() => { setShowCarteiras(false); setShowSettings(false); setShowStats(false); setShowAdminManagement(false); setShowOverview(false); setShowAbout(false);}}
              onOpenSettings={() => { setShowSettings(true); setShowCarteiras(false); setShowStats(false); setShowAdminManagement(false); setShowOverview(false); setShowAbout(false);}}
              onOpenOverview={() => { setShowOverview(true); setShowCarteiras(false); setShowSettings(false); setShowStats(false); setShowAdminManagement(false); setShowAbout(false);}}
              onOpenAdminManagement={() => { setShowAdminManagement(true); setShowCarteiras(false); setShowSettings(false); setShowStats(false); setShowOverview(false); setShowAbout(false); }}
              onOpenAboutPage={() => { setShowAbout(true); setShowCarteiras(false); setShowSettings(false); setShowStats(false); setShowAdminManagement(false); setShowOverview(false); }}
              onOpenQuitarTaxas={() => { setPendingFeeVariant('voluntary'); setShowPendingFeeModal(true); }}
              showingCarteiras={showCarteiras}
              showingSettings={showSettings}
              showingAdminManagement={showAdminManagement}
            />

            <main
              className={`flex-1 w-full py-8 mx-auto ${showAdminManagement ? 'max-w-full px-8 sm:px-12' : 'max-w-4xl px-4 sm:px-6'}`}
            >
              {showStats && isStatsAvailable() ? (
                <Suspense fallback={<Loader text={t('layout.loadingStats')} />}>
                  <StatsScreen onBack={() => setShowStats(false)} />
                </Suspense>
              ) : showAdminManagement ? (
                <AdminManagement onBack={() => setShowAdminManagement(false)} />
              ) : showOverview ? (
                <AppOverviewScreen onBack={() => setShowOverview(false)} />
              ) : showSettings ? (
                <SettingsScreen onBack={() => setShowSettings(false)} />
              )  : showAbout ? (
                <AboutPage onBack={() => setShowAbout(false)} />
              ) : showCarteiras ? (
                <WalletList
                  onClose={() => { setShowCarteiras(false); setAddressToAdd(null); setWalletDataToAdd(null); }}
                  addressToAdd={addressToAdd}
                  walletDataToAdd={walletDataToAdd}
                  clearAddressToAdd={() => setAddressToAdd(null)}
                  clearWalletDataToAdd={() => setWalletDataToAdd(null)}
                />
              ) : (
                <TransferForm
                  onOpenCarteiras={(address, walletData) => {
                    setAddressToAdd(address ?? walletData?.address ?? null);
                    setWalletDataToAdd(walletData ?? null);
                    setShowCarteiras(true);
                  }}
                  onOpenPendingFeeModal={(variant) => {
                    setPendingFeeVariant(variant || 'blocking');
                    setShowPendingFeeModal(true);
                  }}
                />
              )}
            </main>

            <Footer onOpenStats={isStatsAvailable() ? () => setShowStats(true) : null} />
          </div>
          </OpenWalletListProvider>
          </NotificationsProvider>
        </TransferButtonLogProvider>
        </SettingsProvider>
        </PendingFeeProvider>
      </IncomingTransferProvider>
    </WalletConnectionProvider>
  );
}
